import React, { useEffect, useState } from 'react';
import { X, History, RotateCcw, Loader2, Clock, User, GitCompare } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { tc } from './theme';
import { useToast } from '../../hooks/use-toast';

/**
 * PageVersions — right-hand drawer listing a page's saved versions.
 *
 * Every page save snapshots the PRE-edit sections (backend
 * /pages/{slug}/versions). Here the admin can:
 *   * see the version timeline (who saved, when, section count)
 *   * preview a version's field values
 *   * diff a version against the live page (text fields only)
 *   * restore — which itself snapshots current state first, so it's safe
 */
function flattenFields(sections) {
  // Map every field to a "section.field" → value string, for diffing.
  const out = {};
  for (const s of sections || []) {
    for (const f of (s.fields || [])) {
      const key = `${s.title || s.id}.${f.label || f.key}`;
      out[key] = typeof f.value === 'string' ? f.value : JSON.stringify(f.value);
    }
  }
  return out;
}

function diffMaps(oldMap, newMap) {
  const keys = Array.from(new Set([...Object.keys(oldMap), ...Object.keys(newMap)]));
  const changes = [];
  for (const k of keys) {
    const a = oldMap[k] ?? '';
    const b = newMap[k] ?? '';
    if (a !== b) changes.push({ key: k, before: a, after: b });
  }
  return changes;
}

export default function PageVersions({ slug, theme, onClose, onRestored }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [diff, setDiff] = useState(null); // { version, changes }

  useEffect(() => {
    let on = true;
    api.get(`/api/admin-v2/pages/${slug}/versions`)
      .then((r) => { if (on) setItems(r.data?.items || []); })
      .catch((err) => toast({ title: 'Failed to load history', description: formatApiError(err) }))
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [slug, toast]);

  const showDiff = async (v) => {
    setBusyId(v.id);
    try {
      const [verRes, liveRes] = await Promise.all([
        api.get(`/api/admin-v2/pages/${slug}/versions/${v.id}`),
        api.get(`/api/admin-v2/pages/${slug}`),
      ]);
      const changes = diffMaps(
        flattenFields(verRes.data.sections),
        flattenFields(liveRes.data.sections),
      );
      setDiff({ version: v, changes });
    } catch (err) {
      toast({ title: 'Diff failed', description: formatApiError(err) });
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (v) => {
    if (!window.confirm(`Restore this page to the version saved ${new Date(v.created_at).toLocaleString()}? Current state is snapshotted first, so you can undo.`)) return;
    setBusyId(v.id);
    try {
      const res = await api.post(`/api/admin-v2/pages/${slug}/versions/${v.id}/restore`);
      toast({ title: 'Page restored' });
      onRestored?.(res.data);
    } catch (err) {
      toast({ title: 'Restore failed', description: formatApiError(err) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/60 backdrop-blur-sm" />
      <aside className="w-full max-w-lg bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><History size={18} /> Version history</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-300"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 size={18} className="animate-spin inline mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No saved versions yet. They appear here the next time you save this page.</p>
        ) : !diff ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500 mb-3">Last {items.length} versions. Newest first.</p>
            {items.map((v, i) => (
              <div key={v.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                      <Clock size={12} className="text-slate-400" />
                      {new Date(v.created_at).toLocaleString()}
                      {i === 0 && <span className="text-[10px] uppercase tracking-wider bg-violet-500/20 text-violet-200 px-1.5 py-0.5 rounded">latest</span>}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                      <User size={10} /> {v.saved_by || 'system'} · {v.section_count} section{v.section_count === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => showDiff(v)}
                      disabled={busyId === v.id}
                      title="Compare with live"
                      className="w-8 h-8 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 flex items-center justify-center"
                    >
                      {busyId === v.id ? <Loader2 size={13} className="animate-spin" /> : <GitCompare size={13} />}
                    </button>
                    <button
                      onClick={() => restore(v)}
                      disabled={busyId === v.id}
                      title="Restore this version"
                      className="w-8 h-8 rounded-lg bg-violet-500/20 text-violet-200 hover:bg-violet-500/30 flex items-center justify-center"
                    >
                      <RotateCcw size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <button onClick={() => setDiff(null)} className="text-xs text-violet-300 hover:text-violet-200 mb-3 inline-flex items-center gap-1">
              ← Back to versions
            </button>
            <p className="text-sm font-semibold text-white mb-1">
              Changes since {new Date(diff.version.created_at).toLocaleString()}
            </p>
            <p className="text-[11px] text-slate-400 mb-4">
              {diff.changes.length === 0 ? 'No differences — this version matches the live page.' : `${diff.changes.length} field(s) differ. "Before" = this version, "After" = live.`}
            </p>
            <div className="space-y-3">
              {diff.changes.map((c) => (
                <div key={c.key} className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-violet-300 mb-2">{c.key}</p>
                  <div className="space-y-1.5">
                    <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 px-2.5 py-1.5">
                      <p className="text-[10px] font-bold text-rose-300 mb-0.5">— version</p>
                      <p className="text-xs text-rose-100 break-words line-clamp-4">{c.before || <em className="text-rose-400/60">empty</em>}</p>
                    </div>
                    <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1.5">
                      <p className="text-[10px] font-bold text-emerald-300 mb-0.5">+ live now</p>
                      <p className="text-xs text-emerald-100 break-words line-clamp-4">{c.after || <em className="text-emerald-400/60">empty</em>}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-white/10">
              <button
                onClick={() => restore(diff.version)}
                disabled={busyId === diff.version.id}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold disabled:opacity-50"
              >
                {busyId === diff.version.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                Restore this version
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
